import {
  ABUSABLE_AD_RELATIONSHIP_TYPES,
  type AdGraphEdgeDto,
  type AdGraphNodeDto,
  type AdGraphResponseDto,
} from './ad-graph.schema';
import {
  parseAdGraphCrownJewelNodeProperties,
  parseAdGraphKnownNodeProperties,
} from './ad-graph-node-properties.util';
import { parseAdGraphNodeId } from './ad-graph-node-id.util';

const abusableRelationshipTypes = new Set<string>(
  ABUSABLE_AD_RELATIONSHIP_TYPES,
);

export const isAbusableAdRelationshipType = (
  relationshipType: string,
): boolean => abusableRelationshipTypes.has(relationshipType);

export const formatAdRelationshipDisplayLabel = (
  relationshipType: string,
): string =>
  relationshipType
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();

export const getAdGraphNodeObjectId = (
  node: Pick<AdGraphNodeDto, 'id' | 'objectId'>,
): string => {
  if (node.objectId.length > 0) {
    return node.objectId;
  }

  return parseAdGraphNodeId(node.id)?.objectId ?? '';
};

export const isAdGraphCrownJewelNode = (
  node: Pick<AdGraphNodeDto, 'isCrownJewel' | 'properties'>,
): boolean => {
  if (node.isCrownJewel) {
    return true;
  }

  return parseAdGraphCrownJewelNodeProperties(node.properties) !== null;
};

export const isAdGraphOwnedNode = (
  node: Pick<AdGraphNodeDto, 'isOwned'>,
): boolean => node.isOwned;

export const getAdGraphCrownJewelId = (
  node: Pick<AdGraphNodeDto, 'properties'>,
): string | null =>
  parseAdGraphCrownJewelNodeProperties(node.properties)?.crownJewelId ?? null;

export const getAdGraphNodeMetadataSource = (
  node: Pick<AdGraphNodeDto, 'properties'>,
): 'crown-jewel' | 'network' | null =>
  parseAdGraphKnownNodeProperties(node.properties)?.source ?? null;

export const getAdGraphLinkDisplayLabel = (
  link: Pick<AdGraphEdgeDto, 'displayLabel' | 'label' | 'relationshipType'>,
): string =>
  link.displayLabel ||
  link.label ||
  formatAdRelationshipDisplayLabel(link.relationshipType);

export const mergeAdGraphResponses = (
  base: AdGraphResponseDto,
  incoming: AdGraphResponseDto,
): AdGraphResponseDto => {
  const nodeById = new Map(base.nodes.map((node) => [node.id, node]));

  for (const node of incoming.nodes) {
    nodeById.set(node.id, node);
  }

  const edgeById = new Map(base.edges.map((edge) => [edge.id, edge]));

  for (const edge of incoming.edges) {
    edgeById.set(edge.id, edge);
  }

  return {
    nodes: [...nodeById.values()],
    edges: [...edgeById.values()],
    truncated: base.truncated || incoming.truncated,
    totalCount: Math.max(base.totalCount, incoming.totalCount),
  };
};

export const getAdGraphEdgeGroupKey = ({
  sourceId,
  targetId,
}: {
  sourceId: string;
  targetId: string;
}): string => [sourceId, targetId].sort().join('|');
